import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import InfiniteScroll from "react-infinite-scroller";
import Dropdown from "react-dropdown";
import { useApolloClient } from "@apollo/react-hooks";
import isEqual from 'lodash/isEqual';

import { InputContainer, Calendar, withQuery } from "components/common";

import { PAYMENTS_LIST_QUERY, PAYMENTS_QUERY } from "./gql-statements";
import PaymentsTable from "./PaymentsTable";
import PaymentForm from "./PaymentForm";
import { track } from "../../../utils/analytics";
import PaymentAdditional from "./PaymentAdditional";

const options = ["SETTLED", "CANCELED", "PENDING", "ALL"];

const Payments = ({ data: { user = {} } = {}, roles }) => {
  const [calendarError, setCalendarError] = useState([]);
  const [payments, setPayments] = useState(user.payments || []);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [status, setStatus] = useState(options[3]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [shouldUpdatePaymentsPage, setUpdatePaymentsPage] = useState(false);
  const client = useApolloClient();

  useEffect(() => {
    if (shouldUpdatePaymentsPage) {
      loadMore();
    }
  }, [shouldUpdatePaymentsPage]);

  const loadMore = async () => {
    console.log("load more called");
    if (loading) {
      return;
    }
    console.log("Loading additional payments...");
    setLoading(true);

    if (shouldUpdatePaymentsPage) {
      const { data } = await client.query({
        query: PAYMENTS_LIST_QUERY,
        fetchPolicy: "network-only",
        variables: {
          status: "ALL",
          fromDate: null,
          toDate: null,
          page: 0,
          perPage: 20,
        },
      });
      const newestPayment = data.payments[0];
      const mostRecentPayment = payments[0];
      if (!isEqual(newestPayment, mostRecentPayment)) {
        setPayments(
          [newestPayment, ...payments].filter(
            payment =>
              `${payment.status || ""}`.toLocaleLowerCase() !== "canceled"
          )
        );
      }
      setUpdatePaymentsPage(false);
      setLoading(false);
    } else {
      client
        .query({
          query: PAYMENTS_LIST_QUERY,
          fetchPolicy: "network-only",
          variables: {
            status,
            fromDate,
            toDate,
            page,
          },
        })
        .then((x) => {
          const {
            data: { payments: p = [] },
          } = x;
          setLoading(false);
          if (p.length) {
            setPage(page + 1);
            setPayments(
              payments
                .concat([...p])
                .filter(
                  (payment) =>
                    `${payment.status || ""}`.toLocaleLowerCase() !== "canceled",
                ),
            );
          } else {
            console.log("Reached end of payments");
            setHasMore(false);
          }
        });
    }
  };

  const loader = () => <div className="loader m-auto h-16 w-16" key={0} />;

  track("DASHBOARD_LOADED_PAYMENTS_PAGE");

  return (
    <InfiniteScroll
      pageStart={page}
      loadMore={loadMore}
      hasMore={hasMore}
      loader={loader()}
    >
      <div className="flex mx-auto self-center">
        <div className="flex flex-col flex-1">
          <h1 className="hidden md-flex">Make a Payment</h1>
          <div className="flex flex-col mb-8 md:-mx-2 md:flex-row">
            {/* <PaymentForm
              className="flex mb-4 md:mb-0 md:flex-auto md:mx-2 md:w-3/5"
              onDrawerClose={() => {
                setUpdatePaymentsPage(true);
              }}
              user={user}
              hideAutopayDescription={roles.user_terminated_with_balance}
            /> */}
            <PaymentAdditional
              className="flex mb-2 md:mb-0 md:flex-auto md:mx-2 md:w-2/5"
              user={user}
            />
          </div>
          <h1 className="mt-6 md:mt-2 mb-1">Payment activity</h1>
          <div className="">
            <div className="flex flex-col md:flex-row w-full">
              <Calendar
                onCalendarError={(err) => {
                  console.log("calendar error", err);
                  setCalendarError(err);
                }}
                onCalendarChange={(from, to) => {
                  const newFromDate = from && from.toDate();
                  const newToDate = to && to.toDate();
                  setFromDate(newFromDate);
                  setToDate(newToDate);
                  setPage(0);
                  setLoading(true);
                  setPage(0);
                  setHasMore(true);
                  setPayments([]);
                  client
                    .query({
                      query: PAYMENTS_LIST_QUERY,
                      variables: {
                        status,
                        fromDate: newFromDate,
                        toDate: newToDate,
                        page: 0,
                      },
                    })
                    .then((res) => {
                      const pmts = res?.data?.payments ?? [];
                      setPage(1);
                      setLoading(false);
                      setPayments(pmts);
                      track("DASHBOARD_PAYMENTS_FILTER_APPLIED");
                    });
                }}
              />
              <InputContainer
                className="w-full mt-2 relative md:ml-3 md:pl-4 md:w-auto md:mt-0"
                style={{ padding: 0, minWidth: "200px" }}
              >
                <Dropdown
                  className="w-full cursor-pointer"
                  controlClassName="border-none cursor-pointer px-4 bg-transparent"
                  menuClassName="Dropdown-shadow"
                  options={options}
                  placeholder={status}
                  onChange={({ value }) => {
                    setStatus(value);
                    setPage(0);
                    setLoading(true);
                    setPage(0);
                    setHasMore(true);
                    setPayments([]);
                    client
                      .query({
                        query: PAYMENTS_LIST_QUERY,
                        variables: {
                          status: value,
                          fromDate,
                          toDate,
                          page: 0,
                        },
                      })
                      .then((res) => {
                        const pmts = res?.data?.payments ?? [];
                        setPage(1);
                        setLoading(false);
                        setPayments(pmts);
                        track("DASHBOARD_PAYMENTS_FILTER_APPLIED");
                      })
                      .catch((err) => {
                        console.log("payments error", err);
                      });
                  }}
                />

                <div
                  className="flex items-center absolute"
                  style={{ zIndex: -1 }}
                >
                  <i className="fas fa-chevron-down text-gray-secondary" />
                </div>
              </InputContainer>
            </div>
            {!!calendarError.length &&
              calendarError.map(({ message }) => <div>{message}</div>)}
          </div>
          <PaymentsTable className="" payments={payments} user={user} />
        </div>
      </div>
    </InfiniteScroll>
  );
};

export default compose(
  connect((state) => ({
    userId: state.user.id,
    roles: state.user.roles,
  })),
  withQuery(PAYMENTS_QUERY),
)(Payments);
